<template>
  <div class="chat-footer" @keydown="activateInput">
    <div
      class="pre-image-upload"
      v-if="attachment.file || (attachment.files && attachment.files.length)"
    >
      <template v-if="attachment.file">
        <div class="image-upload">
          <img
            v-if="isImage(attachment.file.name)"
            :src="attachment.fileUrl"
            class="img-fluid"
          />
          <video v-else-if="isVideo(attachment.file.name)" height="50">
            <source :src="createObjectURL(attachment.file)" />
            Your browser does not support HTML5 video.
          </video>
          <div
            :style="{ padding: '8px 3px', position: 'relative' }"
            v-else-if="isDoc(attachment.file.name)"
          >
            <file-icon :style="{ color: 'gray' }" />
            <span class="file-ext" :style="{ color: 'white' }">
              {{ getFileExtDisplay(attachment.file.name) }}
            </span>
          </div>
          <p>
            {{ attachment.fileName }}

            <small class="d-block text-danger" v-if="attachment.isExceedLimit">
              {{
                $t('error.fileExceed', { size: byteToMB(attachment.maxSize) })
              }}
            </small>
          </p>
        </div>
        <feather-icon
          icon="XIcon"
          class="cursor-pointer"
          size="22"
          @click="removeAttachment"
        />
      </template>
      <template v-else>
        <div
          class="multi-upload"
          style="display: flex; gap: 8px; flex-wrap: wrap; align-items: center"
        >
          <div
            class="image-upload"
            v-for="(file, idx) in attachment.files"
            :key="`multi-${idx}`"
            style="display: flex; align-items: center; gap: 8px"
          >
            <img
              v-if="isImage(file.name)"
              :src="createObjectURL(file)"
              class="img-fluid"
              style="
                max-height: 50px;
                max-width: 70px;
                object-fit: cover;
                border-radius: 4px;
              "
            />
            <video v-else-if="isVideo(file.name)" height="50">
              <source :src="createObjectURL(file)" />
              Your browser does not support HTML5 video.
            </video>
            <div :style="{ padding: '8px 3px', position: 'relative' }" v-else>
              <file-icon :style="{ color: 'gray' }" />
              <span class="file-ext" :style="{ color: 'white' }">
                {{ getFileExtDisplay(file.name) }}
              </span>
            </div>
            <p
              style="
                margin: 0;
                font-size: 12px;
                max-width: 160px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              "
            >
              {{ file.name }}
            </p>
          </div>
        </div>
        <p style="margin-top: 6px; margin-bottom: 0; font-size: 12px">
          {{ attachment.fileName }}
        </p>
        <feather-icon
          icon="XIcon"
          class="cursor-pointer"
          size="22"
          style="flex-shrink: 0"
          @click="removeAttachment"
        />
      </template>
    </div>
    <div class="emoji-picker" v-if="isShowEmojiPicker">
      <div class="row justify-content-center">
        <div
          class="col col-auto emoji cursor-pointer"
          v-for="(emo, key) in emoji"
          :key="`emoji-${key}`"
          @click="addEmoji(emo)"
        >
          {{ emo }}
        </div>
      </div>
    </div>
    <div v-if="isShowTranslateToggle">
      <span class="quick-msg-translate">
        {{ this.translate.translatedMessage }}
      </span>
    </div>
    <div class="quick-msg" v-if="isShowQuickMsgToggle">
      <ul
        id="list"
        :style="{
          marginBottom: '0rem',
          marginTop: '0rem',
          marginLeft: '-2.9rem'
        }"
      >
        <li
          class="msg-title-message"
          ref="inputBox"
          v-for="(msg, index) in quickMessages"
          @mousedown.prevent
          @click="selectQuickMessage(msg.id)"
          :class="{ focus: index === focus }"
        >
          <a class="text-msg-title">
            {{ getQuickMessageTitle(msg) }}
          </a>
        </li>
        <li class="msg-title-message" v-if="quickMessages.length == 0">
          <a class="text-msg-title">
            {{ $t('general.noQuickMessage') }}
          </a>
        </li>
      </ul>
    </div>
    <b-form class="chat-app-form" @submit.prevent="sendMessage">
      <div class="resizer" @mousedown="initDrag"></div>
      <b-input-group class="input-group-merge form-send-message mr-1">
        <div class="quick-reply">
          <b-spinner
            v-if="translateLoading"
            style="margin-top: 10px"
            variant="primary"
            label="Spinning"
            small
          ></b-spinner>
          <div v-else @click="openQuickReply">
            <QuickReplyIcon
              :style="{
                color: isShowQuickMsgToggle ? 'var(--primary)' : 'inherit'
              }"
            />
          </div>
        </div>
        <b-form-textarea
          ref="inputBox"
          @focus="onInputFocus"
          @input="startTyping"
          tabindex="0"
          @keyup="keyAction"
          @keydown="keyDownAction"
          @keydown.enter.exact.prevent="sendMessage"
          v-debounce:2000ms="stopTyping"
          v-model="text"
          rows="2"
          max-rows="2"
          no-resize
          trim
          :autofocus="activeChannel.operatorAssigned == userData.id"
          :placeholder="$t('general.enterYourMessage')"
          @click="closeEmoji"
        />

        <div class="chat-input-actions">
          <div class="chat-input-actions__items">
            <feather-icon
              icon="SmileIcon"
              class="cursor-pointer mr-1"
              size="22"
              :style="{
                color: isShowEmojiPicker ? 'var(--primary)' : 'inherit'
              }"
              @click="openEmoji"
            />
            <b-button
              variant="primary"
              type="submit"
              v-if="
                text ||
                attachment.file ||
                (attachment.files && attachment.files.length)
              "
            >
              {{ $t('chat.send') }}
            </b-button>
            <feather-icon
              icon="PaperclipIcon"
              class="cursor-pointer mr-1"
              size="22"
              @click="openAttachment"
              v-else
            />
          </div>
        </div>
      </b-input-group>
    </b-form>
    <input
      type="file"
      style="display: none"
      name="fields[assetsFieldHandle][]"
      id="assetsFieldHandle"
      class="w-px h-px opacity-0 overflow-hidden absolute"
      @change="onAttachmentChange"
      ref="fileInput"
      multiple
    />
    <!-- accept="image/*,video/*,.xlsx,.xls,.csv,.pdf,.docx,.doc" -->

    <multi-file-pre-upload-modal
      v-model="isMultiPreModalVisible"
      :files="tempSelectedFiles"
      @confirm="onConfirmMultiFiles"
      @cancel="onCancelMultiFiles"
    />
  </div>
</template>

<script>
  import store from '@/store';
  import _ from 'lodash';
  import {
    BAvatar,
    BDropdown,
    BDropdownItem,
    BForm,
    BInputGroup,
    BFormInput,
    BButton,
    BBadge,
    BSpinner,
    BFormTextarea
  } from 'bootstrap-vue';
  import emoji from '@/data/emoji';
  import config from '@/data/config';
  import Repository from '@/repositories/RepositoryFactory';
  import {
    isVideo,
    isDoc,
    isImage,
    isCsv,
    isExcel,
    isPdf,
    isWord,
    getFileExtDisplay
  } from '../../helpers/fileHelper';
  import CsvIcon from '@/components/icons/CsvIcon.vue';
  import FileIcon from '@/components/icons/FileIcon.vue';
  import ExcellIcon from '@/components/icons/ExcellIcon.vue';
  import MsWordIcon from '@/components/icons/MsWordIcon.vue';
  import PdfIcon from '@/components/icons/PdfIcon.vue';
  import QuickReplyIcon from '@/components/icons/QuickReplyIcon.vue';
  import useJwt from '@/auth/jwt/useJwt';
  import MultiFilePreUploadModal from '@/components/MultiFilePreUploadModal.vue';

  const ChatMessageRepository = Repository.get('chatMessage');
  const QuickMessageRepository = Repository.get('quickMessage');
  const TranslateRepository = Repository.get('translateRepository');
  export default {
    components: {
      BAvatar,
      BDropdown,
      BDropdownItem,
      BForm,
      BInputGroup,
      BFormInput,
      BButton,
      BBadge,
      BSpinner,
      BFormTextarea,
      BSpinner,

      CsvIcon,
      PdfIcon,
      ExcellIcon,
      MsWordIcon,
      FileIcon,
      QuickReplyIcon,
      MultiFilePreUploadModal
    },
    props: {
      activeChannel: {
        type: Object,
        default: () => {
          return {};
        }
      },
      toggleRightSidebar: {
        type: Function,
        require: true
      }
    },
    data() {
      return {
        attachment: {},
        isShowEmojiPicker: false,
        isTyping: false,
        isTranslating: false,

        emoji,
        config,

        text: '',
        translate: '',
        isShowTranslateToggle: false,
        isShowQuickMsgToggle: false,
        quickMessages: [],
        focus: null,
        focusInterval: null,
        qmNoResultCount: 0,
        chatInputHeight: 65,
        footerHeight: localStorage.getItem('chatFooterHeight')
          ? parseInt(localStorage.getItem('chatFooterHeight'))
          : 65,
        isDragging: false,
        startY: 0,
        startPosition: 0,
        translateLoading: false,
        isMultiPreModalVisible: false,
        tempSelectedFiles: []
      };
    },

    watch: {
      text(value) {
        // if (value.trim().startsWith("#")) {
        //   this.focus = 0;
        //   this.getQuickMessages(value.trim());
        // } else {
        this.isShowQuickMsgToggle = false;
        if (this.activeChannel?.isAutoTranslate) {
          this.isTranslating = true;
          this.manualTranslate(value);
        }
        // }
      },
      activeChannel(value, oldValue) {
        this.$nextTick(() => {
          this.bindText(value, oldValue);
        });
        if (value) {
          this.isTranslating = value?.isAutoTranslate;
          if (!this.isTranslating) {
            this.translate = '';
            this.isShowTranslateToggle = false;
            this.translateLoading = false;
          }
        }
      }
    },
    computed: {
      locale() {
        return this.$i18n.locale.split('-')[0]
      },
      userData: function () {
        return this.$store.state.profile.email ||
          this.$store.state.profile.phone
          ? this.$store.state.profile
          : JSON.parse(
              localStorage.getItem(useJwt.jwtConfig.storageUserDataKeyName)
            );
      },
      chatSetting: function () {
        return JSON.parse(localStorage.getItem('chatSettings'));
      }
    },
    setup() {
      return {
        isImage,
        isVideo,
        isDoc,
        isCsv,
        isExcel,
        isPdf,
        isWord,
        getFileExtDisplay
      };
    },
    mounted() {
      this.bindText(this.activeChannel);
      window.addEventListener('paste', this.handlePaste);
      window.addEventListener('keypress', this.activateInput);
      document.addEventListener('mousemove', this.doDrag);
      document.addEventListener('mouseup', this.stopDrag);
      this.$nextTick(() => {
        this.resizeChatAction();
        this.resizeFooterHeight();
        this.resizeChatBody();
      });
    },
    destroyed() {
      this.bindText(null, this.activeChannel);
      window.removeEventListener('paste', this.handlePaste);
      window.removeEventListener('keypress', this.activateInput);
      document.removeEventListener('mousemove', this.doDrag);
      document.removeEventListener('mouseup', this.stopDrag);
    },
    methods: {
      getQuickMessageTitle(msg) {
        const title = msg.titleTranslate || msg.title 
        if (typeof title === 'object') {
          return title[this.locale] || title.en || title.zh || title.km || title.vi || title
        }
        return title
      },
      openQuickReply() {
        if (!this.activeChannel.operatorAssigned) {
          this.$refs.inputBox.focus();
          return;
        }
        if (this.isShowEmojiPicker) {
          return;
        }
        if (this.attachment.fileUrl) {
          return;
        }

        if (this.isShowQuickMsgToggle) {
          this.isShowQuickMsgToggle = false;
          return;
        }

        this.focus = 0;
        this.getQuickMessages('');
      },
      bindText(value, oldValue) {
        if (oldValue) {
          store.commit('app/UPDATE_CHAT_DRAFT', {
            key: `draft_${oldValue.id}`,
            value: this.text?.trim() || undefined
          });
        }
        this.text = store.state.app.chatDraft[`draft_${value?.id}`] || '';
      },
      activateInput(event) {
        if (event.key === 'R') {
          if (document.activeElement === this.$refs.inputBox.$el) {
            return;
          }
          this.$refs.inputBox.focus();
          event.preventDefault();
        } else if (event.key === 'F2') {
          if (document.activeElement !== this.$refs.inputBox.$el) {
            return;
          }
          event.preventDefault();
          this.$refs.inputBox.blur();
        }
      },
      keyAction: function (event) {
        switch (event.keyCode) {
          // 38 — up
          case 38:
            clearInterval(this.focusInterval);
            break;

          // 40 — down
          case 40:
            clearInterval(this.focusInterval);
            break;

          case 13:
            this.$nextTick(() => {
              if (this.isShowQuickMsgToggle) {
                this.selectQuickMessage(this.quickMessages[this.focus]['id']);
              }
            });

            break;
        }
      },

      keyDownAction: function (event) {
        switch (event.keyCode) {
          // 38 — up
          case 38:
            if (this.focus === null) {
              this.focus = 0;
            }

            if (this.focus > 0) this.focus--;
            else this.focus = this.quickMessages.length - 1;

            break;

          // 40 — down
          case 40:
            if (this.focus === null) {
              this.focus = 0;
            }

            if (this.focus < this.quickMessages.length - 1) this.focus++;
            else this.focus = 0;

            break;
        }
      },

      selectQuickMessage(id) {
        let msg = this.quickMessages.filter(function (el) {
          return el.id === id;
        });
        let desc = msg[0].description ? msg[0].description : msg[0].title;
        if (typeof desc === 'object') {
          desc = desc?.[this.locale] || desc?.en || desc?.zh || desc?.km || desc?.vi || desc
        }
        this.text = desc;
        this.isShowQuickMsgToggle = false;
      },

      async getQuickMessages(textSearch) {
        const keyword = textSearch.replace('#', '');

        let payload = {
          text: textSearch.replace('#', '')
        };

        let response = await QuickMessageRepository.search(payload);

        const { data } = response;

        this.quickMessages = data;

        if (data.length === 0) {
          this.qmNoResultCount += 1;
        } else {
          this.qmNoResultCount = 0;
        }

        this.isShowQuickMsgToggle = this.qmNoResultCount < 2;
      },
      handlePaste(e) {
        if (e.clipboardData.files.length > 0) {
          this.$refs.fileInput.files = e.clipboardData.files;
          this.onAttachmentChange();
          setTimeout(() => {
            this.text = '';
          }, 5);
        }
      },
      createObjectURL(file) {
        return window.URL.createObjectURL(file);
      },
      byteToMB(size) {
        return size / 1000 / 1000;
      },
      openAttachment() {
        if (!this.activeChannel.operatorAssigned) {
          this.$refs.inputBox.focus();
          return;
        }
        if (this.isShowEmojiPicker) {
          return;
        }
        this.$refs.fileInput.value = null;
        this.$refs.fileInput.click();
      },
      removeAttachment() {
        this.$refs.fileInput.value = null;
        this.attachment = {};
      },
      onAttachmentChange() {
        this.isShowQuickMsgToggle = false;
        const self = this;
        const filelist = [...this.$refs.fileInput.files];
        if (!filelist.length) return;

        if (filelist.length === 1) {
          const file = filelist[0];
          if (file) {
            if (isImage(file.name)) {
              var reader = new FileReader();
              reader.onload = e => {
                self.attachment = {
                  file,
                  fileUrl: e.target.result,
                  fileName: file.name,
                  fileSize: file.size,
                  maxSize: config.maxImageSize,
                  isExceedLimit: file.size > config.maxImageSize
                };
              };
              reader.readAsDataURL(file);
            } else if (isVideo(file.name)) {
              self.attachment = {
                file,
                fileName: file.name,
                fileSize: file.size,
                maxSize: config.maxVideoSize,
                isExceedLimit: file.size > config.maxVideoSize
              };
            } else if (isDoc(file.name)) {
              self.attachment = {
                file,
                fileName: file.name,
                fileSize: file.size,
                maxSize: config.maxDocSize,
                isExceedLimit: file.size > config.maxDocSize
              };
            } else {
              console.log(isDoc(file.name));
              return;
            }
          }
        } else {
          // Multiple files selected -> open pre-upload modal
          this.tempSelectedFiles = filelist;
          this.isMultiPreModalVisible = true;
        }
      },
      onConfirmMultiFiles(files) {
        if (!files || !files.length) {
          this.isMultiPreModalVisible = false;
          return;
        }
        // If only one file remains, treat as single attachment (mediaId flow)
        if (files.length === 1) {
          const file = files[0];
          const payload = { file, fileName: file.name };
          if (this.isImage(file.name)) {
            // preview
            payload.fileUrl = this.createObjectURL(file);
          }
          this.$emit('send-attachment', payload);
        } else {
          const displayName = `${files[0].name} +${files.length - 1}`;
          // Send immediately
          this.$emit('send-attachment', { files, fileName: displayName });
        }
        this.attachment = {};
        this.isMultiPreModalVisible = false;
        this.tempSelectedFiles = [];
        this.$refs.fileInput.value = null;
      },
      onCancelMultiFiles() {
        this.isMultiPreModalVisible = false;
        this.tempSelectedFiles = [];
        this.$refs.fileInput.value = null;
      },
      closeEmoji() {
        this.isShowEmojiPicker = false;
      },
      openEmoji() {
        if (!this.activeChannel.operatorAssigned) {
          this.$refs.inputBox.focus();
          return;
        }
        if (this.attachment.fileUrl) {
          return;
        }
        this.isShowEmojiPicker = !this.isShowEmojiPicker;
      },
      addEmoji(emoji) {
        this.text += emoji;
      },
      sendMessage() {
        if (this.isTranslating) return; // If translation have finish don't wallow to send message
        if (this.isShowQuickMsgToggle) return;
        this.isShowEmojiPicker = false;
        if (
          (this.attachment.files && this.attachment.files.length) ||
          this.attachment.file
        ) {
          if (this.attachment.file && this.attachment.isExceedLimit) {
            return;
          }
          this.$emit('send-attachment', this.attachment);
          this.attachment = {};
        } else if (this.text.trim()) {
          this.$emit('send-text', this.text, this.translate);
          this.text = '';
        }
      },
      startTyping() {
        if (this.isTyping) {
          return;
        }
        this.typing(true);
      },
      stopTyping() {
        if (!this.isTyping) {
          return;
        }
        this.typing(false);
      },
      manualTranslate: _.debounce(async function (text) {
        this.translateLoading = true;
        if (text) {
          const response = await TranslateRepository.translate({
            text,
            target: this.activeChannel.detectedLanguage
          });
          this.translate = response?.data?.data;
          this.isShowTranslateToggle = true;
        } else {
          this.translate = '';
          this.isShowTranslateToggle = false;
        }
        this.isTranslating = false;
        this.translateLoading = false;
      }, 200),

      typing(isTyping = false) {
        this.isTyping = isTyping;
        if (!this.activeChannel?.token) {
          return;
        }

        ChatMessageRepository.typing({
          token: this.activeChannel.token,
          isTyping: isTyping
        })
          .then(response => {})
          .catch(err => {});
      },
      async onInputFocus(ev) {
        if (this.activeChannel?.isAutoTranslate && this.text.length > 0) {
          this.isShowTranslateToggle = true;
          this.manualTranslate(this.text);
        } else {
          this.isShowTranslateToggle = false;
        }
        if (!this.activeChannel.operatorAssigned) {
          this.$refs.inputBox.blur();
          ev.preventDefault();
          this.$bvModal
            .msgBoxConfirm(this.$t('alert.autoAssignIfReply'), {
              title: this.$t('alert.areYouSure'),
              size: 'sm',
              buttonSize: 'sm',
              okVariant: 'primary',
              okTitle: <span ref="bvAgreeBtn">{this.$t('button.agree')}</span>,
              cancelTitle: this.$t('button.cancel'),
              cancelVariant: 'outline-secondary',
              hideHeaderClose: false,
              centered: true
            })
            .then(value => {
              if (value) {
                this.$emit('auto-assign');
                this.activeChannel.operatorAssigned = this.userData.id;
                this.$refs.inputBox.focus();
              }
            });
        }
      },
      resizeFooterHeight() {
        let chatFooterEl = document.getElementsByClassName('chat-footer');
        if (chatFooterEl.length > 0) {
          chatFooterEl[0].style.height = this.footerHeight + 'px';
        }
      },
      resizeChatAction() {
        this.resizeChatBody();
        let chatFooterEl = document.getElementsByClassName('chat-footer');
        let chatActionEl =
          document.getElementsByClassName('chat-input-actions');
        let quickReplyEl = document.getElementsByClassName('quick-reply');
        if (chatFooterEl.length > 0 && chatActionEl.length > 0) {
          let chatFooterHeight = chatFooterEl[0].offsetHeight;
          if (this.footerHeight > 65) {
            chatActionEl[0].style.marginBottom = 20 + 'px';
            chatActionEl[0].style.alignItems = 'flex-end';
            quickReplyEl[0].style.marginBottom = 20 + 'px';
            quickReplyEl[0].style.alignItems = 'flex-end';
          } else {
            chatActionEl[0].style.marginBottom = 0 + 'px';
            chatActionEl[0].style.alignItems = 'center';
            quickReplyEl[0].style.marginBottom = 20 + 'px';
            quickReplyEl[0].style.alignItems = 'flex-end';
          }
        }
      },
      resizeChatBody() {
        let messageBodyEl = document.getElementsByClassName('messages-body');
        let chatFooterEl = document.getElementsByClassName('chat-footer');
        let userChat = document.getElementsByClassName('user-chats');
        if (messageBodyEl.length > 0 && chatFooterEl.length > 0) {
          messageBodyEl[0].style.height = `calc(100% - ${
            this.footerHeight >= 65 ? this.footerHeight : 75
          }px)`;
          // messageBodyEl[0].style.maxHeight = messageBodyEl[0].scrollHeight + "px";
          if (
            this.isTranslate() &&
            userChat.length > 0 &&
            this.footerHeight <= 65
          ) {
            userChat[0].style.paddingBottom = 45 + 'px';
            this.$nextTick(() => {
              // this.$emit('scroll-to-bottom');
            });
          }
        }
      },
      initDrag(e) {
        this.isDragging = true;
        this.startY = e.clientY;
        let chatFooterEl = document.getElementsByClassName('chat-footer');
        if (chatFooterEl.length > 0) {
          this.startPosition = -chatFooterEl[0].offsetHeight;
        }
        const overlay = document.createElement('div');
        overlay.id = 'resize-overlay';
        overlay.style.footerHeight = 'fixed';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.right = '0';
        overlay.style.bottom = '0';
        overlay.style.zIndex = '9999';
        document.body.appendChild(overlay);

        document.body.style.cursor = 'row-resize';
        document.body.style.userSelect = 'none';
      },

      doDrag(e) {
        if (!this.isDragging) return;
        this.$emit('resize-chat-box', true);

        const delta = this.startY - e.clientY;
        const newPosition = -(this.startPosition - delta);

        // Add constraints
        const minPosition = 65;
        const maxPosition = 400;

        let chatFooterEl = document.getElementsByClassName('chat-footer');
        if (chatFooterEl.length > 0) {
          const constrainedHeight = Math.min(
            Math.max(newPosition, minPosition),
            maxPosition
          );
          chatFooterEl[0].style.height = `${constrainedHeight}px`;
          this.footerHeight = constrainedHeight;
          this.resizeChatBody();
          this.resizeChatAction();
        }
      },
      stopDrag() {
        this.isDragging = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        localStorage.setItem('chatFooterHeight', this.footerHeight);

        // Remove overlay
        const overlay = document.getElementById('resize-overlay');
        if (overlay) {
          overlay.remove();
          this.$emit('resize-chat-box', true);
        }
      },
      isTranslate() {
        if (
          this.activeChannel.isAutoTranslate &&
          this.activeChannel.operatorAssigned !== this.userData.id
        ) {
          return true;
        }
        const chatSetting =
          JSON.parse(localStorage.getItem('chatSettings')).isTranslationOn == 1;
        const chatChannel = this.activeChannel.state != 1; // `1` = channel is opening
        const isTransferToMerchant = this.isTransfer;
        const isAsignToOtherOperator =
          this.activeChannel.operatorAssigned != this.userData.id;
        return chatChannel || isTransferToMerchant || isAsignToOtherOperator
          ? false
          : !!chatSetting;
      }
    }
  };
</script>

<style lang="scss">
  .chat-app-form {
    position: relative;
    textarea.form-control {
      padding: 8px !important;
      overflow-y: auto !important;
      border: unset !important;
      box-shadow: unset !important;
    }

    .input-group:not(.bootstrap-touchspin):focus-within {
      box-shadow: unset !important;
    }
  }

  li.focus {
    border-bottom: 1px solid red !important;
    background: #a6669d;
    color: white;
  }

  li.msg-title-message {
    border-bottom: 1px solid #f0f0f0 !important;
    margin-bottom: 0rem;
    display: inherit;
  }

  a.text-msg-title {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    padding: 3px;
    padding-left: 10px;
    font-size: 13px;
  }

  .chat-input-actions {
    display: flex;
    align-items: center;
    justify-items: center;
  }

  .resizer {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 1px;
    background: #e0e0e0;
    cursor: row-resize;
    z-index: 100;
    transform: translateY(-50%);
  }

  .resizer:hover,
  .resizer:active {
    background: #bdbdbd;
  }

  .resizer::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 40px;
    height: 4px;
    background: #9e9e9e;
    border-radius: 2px;
  }

  .quick-reply {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 4px;

    svg {
      cursor: pointer;
    }
  }
</style>
